import React, { useState, useEffect, useRef } from 'react';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  CheckCircle, ArrowRight, Star, Users, Building2, Rocket,
  Brain, Shield, BarChart, Lock, Globe, Headphones, Zap,
  Check, X, ChevronDown, ChevronUp, Play, Sparkles,
  UserCircle, Calendar, FolderOpen, AlertTriangle, Wallet,
  Briefcase, Home, GraduationCap, Settings, Smartphone,
  FileText, Bell, ClipboardList, Heart, Package, Eye,
  TrendingUp, Award, Crown, Target, Layers, Monitor, Search, Cloud
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from 'sonner';
import { useBooking } from '../context/BookingContext';
import { useCMS } from '../context/CMSContext';
import PricingUnlockModal from '../components/PricingUnlockModal';

// Icon mapping for CMS data
const iconMap = {
  Globe, Smartphone, Users, Shield, FileText, Brain, BarChart,
  AlertTriangle, Wallet, ClipboardList, Heart, Settings, Bell,
  GraduationCap, Home, Briefcase, Rocket, TrendingUp, Crown,
  Package, Layers, UserCircle, Calendar, FolderOpen, Monitor, Cloud,
  Lock, Headphones
};

// Animated Counter Component
const AnimatedNumber = ({ value, suffix = '', prefix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    // Check if it's a decimal number
    const hasDecimal = value.includes('.');
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
    
    if (isNaN(numericValue)) {
      setDisplayValue(value);
      return;
    }

    let start = 0;
    const duration = 2000;
    const increment = numericValue / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= numericValue) {
        setDisplayValue(hasDecimal ? numericValue.toFixed(1) : Math.floor(numericValue));
        clearInterval(timer);
      } else {
        setDisplayValue(hasDecimal ? start.toFixed(1) : Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [isVisible, value]);

  return (
    <span ref={ref}>
      {prefix}{displayValue}{suffix}
    </span>
  );
};

// Feature Category Tab Button
const CategoryTab = ({ icon: Icon, label, isActive, onClick, color }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
      isActive 
        ? `bg-${color} text-white shadow-lg shadow-${color}/30 scale-105` 
        : 'bg-white text-text-secondary hover:bg-gray-50 hover:text-primary-navy'
    }`}
  >
    <Icon size={18} />
    <span className="hidden sm:inline">{label}</span>
  </button>
);

// Interactive Feature Card
const FeatureCard = ({ feature, tier, delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const getValue = () => {
    if (feature[tier] === true) return { type: 'check', value: true };
    if (feature[tier] === false) return { type: 'x', value: false };
    return { type: 'text', value: feature[tier] };
  };

  const { type, value } = getValue();
  
  const tierColors = {
    starter: 'care-teal',
    growth: 'tech-blue',
    enterprise: 'purple-600'
  };

  return (
    <div 
      className={`p-4 rounded-xl border transition-all duration-300 cursor-default ${
        isHovered 
          ? `border-${tierColors[tier]} bg-${tierColors[tier]}/5 shadow-lg -translate-y-1` 
          : 'border-border-line bg-white hover:border-gray-300'
      }`}
      style={{ animationDelay: `${delay}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center justify-between">
        <span className={`text-sm font-medium transition-colors ${isHovered ? 'text-primary-navy' : 'text-text-secondary'}`}>
          {feature.name}
        </span>
        {type === 'check' && (
          <div className={`w-6 h-6 rounded-full bg-${tierColors[tier]}/20 flex items-center justify-center`}>
            <Check className={`text-${tierColors[tier]}`} size={14} />
          </div>
        )}
        {type === 'x' && (
          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
            <X className="text-gray-400" size={14} />
          </div>
        )}
        {type === 'text' && (
          <span className={`text-xs font-semibold px-2 py-1 rounded-full bg-${tierColors[tier]}/10 text-${tierColors[tier]}`}>
            {value}
          </span>
        )}
      </div>
    </div>
  );
};

const FeaturesAndPricingPage = () => {
  const { openBooking, openContact, hasContactSubmitted } = useBooking();
  const { getFeaturePackages, getFeatureSections, getFeatureCategories, getFPPageSettings } = useCMS();
  const [activeCategory, setActiveCategory] = useState('platform');
  const [activeTier, setActiveTier] = useState('growth');
  const [pricingUnlocked, setPricingUnlocked] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  // CMS Data States
  const [cmsPackages, setCmsPackages] = useState([]);
  const [cmsSections, setCmsSections] = useState([]);
  const [cmsCategories, setCmsCategories] = useState([]);
  const [pageSettings, setPageSettings] = useState(null);
  const [cmsLoading, setCmsLoading] = useState(true);

  // Default page settings
  const defaultSettings = {
    hero_title: "Everything You Need to Run a Modern NDIS Business",
    hero_subtitle: "One platform. Complete ownership. No per-user fees.",
    hero_badge: "The GetCare 360 Advantage",
    meta_title: "Features & Pricing | GetCare 360",
    meta_description: "Explore GetCare 360 features and transparent pricing for NDIS providers",
    stats: [
      { value: "300", suffix: "+", label: "Features" },
      { value: "100", suffix: "+", label: "NDIS Providers" },
      { value: "4.9", suffix: "/5", label: "Rating" },
      { value: "60", suffix: "%", label: "Time Saved" }
    ],
    trust_badges: [
      { icon: "Shield", text: "NDIS Compliant" },
      { icon: "Lock", text: "100% Data Ownership" },
      { icon: "Globe", text: "Australian Hosted" },
      { icon: "Headphones", text: "Local Support" }
    ],
    explorer_title: "Explore Features by Category",
    explorer_subtitle: "Click on categories to explore features. Select a tier to see what's included.",
    pricing_title: "Choose Your Investment Package",
    pricing_subtitle: "Build once, own forever. No per-user fees, no hidden costs.",
    pricing_badge: "One-Time Investment Model",
    comparison_title: "Complete Feature Comparison",
    comparison_subtitle: "See exactly what's included in each package",
    show_pricing_packages: true
  };

  // Merge CMS settings with defaults (CMS values take precedence)
  const settings = {
    ...defaultSettings,
    ...(pageSettings || {}),
    // Ensure arrays have fallbacks
    stats: (pageSettings?.stats && pageSettings.stats.length > 0) ? pageSettings.stats : defaultSettings.stats,
    trust_badges: (pageSettings?.trust_badges && pageSettings.trust_badges.length > 0) ? pageSettings.trust_badges : defaultSettings.trust_badges,
    show_pricing_packages: pageSettings?.show_pricing_packages !== false
  };

  // Fetch CMS data on mount
  useEffect(() => {
    const fetchCmsData = async () => {
      try {
        const [packages, sections, categories, fpSettings] = await Promise.all([
          getFeaturePackages(true),
          getFeatureSections(true),
          getFeatureCategories(true),
          getFPPageSettings()
        ]);
        setCmsPackages(packages || []);
        setCmsSections(sections || []);
        setCmsCategories(categories || []);
        if (fpSettings) setPageSettings(fpSettings);
      } catch (error) {
        console.log('CMS data not available, using defaults');
      } finally {
        setCmsLoading(false);
      }
    };
    fetchCmsData();
  }, [getFeaturePackages, getFeatureSections, getFeatureCategories, getFPPageSettings]);

  // Check if user is a returning user who has already submitted any form
  const isReturningUser = () => {
    return (
      localStorage.getItem('contact_submitted') === 'true' ||
      localStorage.getItem('booking_submitted') === 'true' ||
      localStorage.getItem('demo_submitted') === 'true' ||
      localStorage.getItem('pricing_unlocked') === 'true' ||
      localStorage.getItem('ai_lead_id') !== null
    );
  };

  useEffect(() => {
    // Unlock pricing for any returning user
    const unlocked = isReturningUser();
    setPricingUnlocked(unlocked);
    if (unlocked) {
      localStorage.setItem('pricing_unlocked', 'true');
    }
  }, [hasContactSubmitted]);

  useEffect(() => {
    if (hasContactSubmitted) {
      setPricingUnlocked(true);
    }
  }, [hasContactSubmitted]);

  const handleUnlockPricing = () => {
    setShowPricingModal(true);
  };

  const handlePricingSuccess = () => {
    setPricingUnlocked(true);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Helper to get icon component from string name
  const getIconComponent = (iconName) => {
    return iconMap[iconName] || Globe;
  };

  // Default feature categories for interactive explorer
  const defaultFeatureCategories = [
    { id: 'platform', label: 'Platform', icon: Globe, color: 'tech-blue' },
    { id: 'mobile', label: 'Mobile', icon: Smartphone, color: 'care-teal' },
    { id: 'participant', label: 'Care Management', icon: UserCircle, color: 'tech-blue' },
    { id: 'staff', label: 'Staff & HR', icon: Users, color: 'heritage-orange' },
    { id: 'ai', label: 'AI Features', icon: Brain, color: 'purple-600' },
    { id: 'compliance', label: 'Compliance', icon: Shield, color: 'care-teal' },
  ];

  // Use CMS categories or fallback to defaults
  const featureCategories = cmsCategories.length > 0 
    ? cmsCategories.map(cat => ({
        id: cat.slug,
        label: cat.name,
        icon: getIconComponent(cat.icon),
        color: cat.color,
        features: cat.features || []
      }))
    : defaultFeatureCategories;

  // Default feature data organized by category
  const defaultFeaturesData = {
    platform: [
      { name: 'Full Platform Ownership', starter: true, growth: true, enterprise: true },
      { name: 'One-Time Build Model', starter: true, growth: true, enterprise: true },
      { name: 'No Per-User Fees', starter: true, growth: true, enterprise: true },
      { name: 'White-Label Branding', starter: 'Basic', growth: 'Full', enterprise: 'Custom UI' },
      { name: 'Custom Domain Setup', starter: true, growth: true, enterprise: true },
      { name: '100% Data Ownership', starter: true, growth: true, enterprise: true },
      { name: 'Data Migration Support', starter: false, growth: true, enterprise: 'Advanced' },
      { name: 'Multi-Entity Setup', starter: false, growth: false, enterprise: true },
    ],
    mobile: [
      { name: 'Branded Web Platform', starter: true, growth: true, enterprise: true },
      { name: 'iOS & Android Mobile App', starter: true, growth: true, enterprise: true },
      { name: 'Push Notifications', starter: false, growth: true, enterprise: true },
      { name: 'Offline Mode', starter: false, growth: 'Basic', enterprise: 'Advanced' },
      { name: 'Geo-Fenced Check-In', starter: false, growth: true, enterprise: true },
      { name: 'Family Portal Access', starter: false, growth: true, enterprise: 'AI Smart' },
      { name: 'Admin Mobile App', starter: false, growth: true, enterprise: true },
    ],
    participant: [
      { name: 'Participant Profiles', starter: true, growth: true, enterprise: true },
      { name: 'Care Plans & Goals', starter: true, growth: true, enterprise: true },
      { name: 'Digital Service Agreements', starter: true, growth: true, enterprise: true },
      { name: 'Progress Notes', starter: true, growth: true, enterprise: true },
      { name: 'Incident Reporting', starter: 'Basic', growth: 'Advanced', enterprise: 'AI Detection' },
      { name: 'Risk Assessment', starter: 'Basic', growth: true, enterprise: 'AI Prediction' },
      { name: 'Funding Tracking', starter: 'Basic', growth: 'Advanced', enterprise: 'AI Forecast' },
      { name: 'NDIS Invoice Generation', starter: true, growth: true, enterprise: true },
    ],
    staff: [
      { name: 'Staff Profiles', starter: true, growth: true, enterprise: true },
      { name: 'Credential Tracking', starter: true, growth: true, enterprise: 'Auto Verify' },
      { name: 'Smart Rostering', starter: 'Basic', growth: 'Advanced', enterprise: 'AI Optimised' },
      { name: 'Timesheet Management', starter: true, growth: true, enterprise: true },
      { name: 'Leave Management', starter: false, growth: true, enterprise: true },
      { name: 'Recruitment Module', starter: 'Basic', growth: true, enterprise: 'Advanced' },
      { name: 'Onboarding Workflows', starter: false, growth: true, enterprise: 'Automated' },
      { name: 'LMS / Training', starter: false, growth: true, enterprise: 'Full LMS' },
    ],
    ai: [
      { name: 'AI Compliance Monitoring', starter: false, growth: true, enterprise: 'Advanced' },
      { name: 'Risk Pattern Detection', starter: false, growth: false, enterprise: true },
      { name: 'Predictive Analytics', starter: false, growth: false, enterprise: true },
      { name: 'Smart Scheduling', starter: false, growth: 'Basic', enterprise: 'Advanced' },
      { name: 'Automated Reporting', starter: 'Basic', growth: 'Advanced', enterprise: 'AI Reports' },
      { name: 'Executive BI Dashboard', starter: false, growth: false, enterprise: true },
      { name: 'AI Document Processing', starter: false, growth: false, enterprise: true },
    ],
    compliance: [
      { name: 'NDIS Compliance Built-In', starter: true, growth: true, enterprise: true },
      { name: 'Role-Based Access (RBAC)', starter: true, growth: true, enterprise: true },
      { name: 'Data Encryption', starter: true, growth: true, enterprise: true },
      { name: 'Audit Logs', starter: true, growth: true, enterprise: 'Advanced' },
      { name: 'Version Tracking', starter: false, growth: true, enterprise: true },
      { name: 'Audit-Ready Reports', starter: 'Basic', growth: 'Advanced', enterprise: 'Automated' },
      { name: 'Compliance Dashboard', starter: false, growth: true, enterprise: 'Executive' },
    ],
  };

  // Build featuresData from CMS categories or use defaults
  const featuresData = cmsCategories.length > 0
    ? cmsCategories.reduce((acc, cat) => {
        acc[cat.slug] = cat.features || [];
        return acc;
      }, {})
    : defaultFeaturesData;

  // Full feature sections for accordion - COMPLETE LIST
  const defaultFeatureSections = [
    {
      id: 'platform',
      title: 'Platform & Ownership',
      icon: Globe,
      color: 'tech-blue',
      features: [
        { name: 'Full Platform Ownership', starter: true, growth: true, enterprise: true },
        { name: 'One-Time Build Model', starter: true, growth: true, enterprise: true },
        { name: 'No Per-User Fees', starter: true, growth: true, enterprise: true },
        { name: 'White-Label Branding', starter: 'Basic Logo', growth: 'Full Branding', enterprise: 'Fully Custom UI' },
        { name: 'Custom Domain Setup', starter: true, growth: true, enterprise: true },
        { name: 'Data Ownership', starter: '100% Yours', growth: '100% Yours', enterprise: '100% Yours' },
        { name: 'Data Migration Support', starter: false, growth: true, enterprise: 'Advanced Migration' },
        { name: 'Multi-Entity Setup', starter: false, growth: false, enterprise: true },
        { name: 'Multi-Site Architecture', starter: false, growth: false, enterprise: true },
      ]
    },
    {
      id: 'mobile',
      title: 'Web & Mobile Capabilities',
      icon: Smartphone,
      color: 'care-teal',
      features: [
        { name: 'Branded Web Platform', starter: true, growth: true, enterprise: true },
        { name: 'Branded Mobile App (iOS & Android)', starter: true, growth: true, enterprise: true },
        { name: 'Push Notifications', starter: false, growth: true, enterprise: true },
        { name: 'Offline Mode', starter: false, growth: 'Basic', enterprise: 'Advanced' },
        { name: 'Geo-Fenced Check-In', starter: false, growth: true, enterprise: true },
        { name: 'Family Portal Access', starter: false, growth: 'Branded', enterprise: 'AI Smart Portal' },
        { name: 'Client Messaging', starter: 'Basic', growth: 'Secure Messaging', enterprise: 'AI-Enhanced' },
        { name: 'Admin App', starter: false, growth: true, enterprise: true },
      ]
    },
    {
      id: 'participant',
      title: 'Participant & Care Management',
      icon: UserCircle,
      color: 'tech-blue',
      features: [
        { name: 'Participant Profiles', starter: true, growth: true, enterprise: true },
        { name: 'Care Plans & Goals', starter: true, growth: true, enterprise: true },
        { name: 'Digital Service Agreements', starter: true, growth: true, enterprise: true },
        { name: 'Digital Signatures', starter: true, growth: true, enterprise: true },
        { name: 'Case & Progress Notes', starter: true, growth: true, enterprise: true },
        { name: 'Incident Reporting', starter: 'Basic', growth: 'Advanced', enterprise: 'AI Pattern Detection' },
        { name: 'Risk Assessment Tools', starter: 'Basic', growth: true, enterprise: 'AI Risk Prediction' },
        { name: 'Funding Tracking', starter: 'Basic', growth: 'Advanced', enterprise: 'AI Forecasting' },
        { name: 'Budget Utilisation Alerts', starter: false, growth: true, enterprise: 'AI Smart Alerts' },
        { name: 'NDIS Invoice Generation', starter: true, growth: true, enterprise: true },
        { name: 'Automated Claim Support', starter: false, growth: true, enterprise: 'Advanced Automation' },
        { name: 'Financial Dashboard', starter: false, growth: true, enterprise: 'Executive BI Dashboard' },
      ]
    },
    {
      id: 'profile',
      title: 'Participant Profile Features',
      icon: Users,
      color: 'care-teal',
      features: [
        { name: 'Full Name, DOB, Age Auto Calculation', starter: true, growth: true, enterprise: true },
        { name: 'NDIS Number', starter: true, growth: true, enterprise: true },
        { name: 'Profile Image', starter: true, growth: true, enterprise: true },
        { name: 'Primary Diagnosis', starter: true, growth: true, enterprise: true },
        { name: 'Care Level Requirements', starter: true, growth: true, enterprise: true },
        { name: 'Support Needs Summary', starter: true, growth: true, enterprise: true },
        { name: 'Support Coordinator Details', starter: true, growth: true, enterprise: true },
        { name: 'Emergency Contact Details', starter: true, growth: true, enterprise: true },
        { name: 'Funding Allocation Overview', starter: 'Basic', growth: 'Advanced', enterprise: 'Predictive Forecast' },
        { name: 'Funding Category Breakdown', starter: 'Core Only', growth: 'Core + CB + Capital', enterprise: 'Full + AI Validation' },
        { name: 'Funding Expiry Alerts', starter: false, growth: true, enterprise: true },
        { name: 'Billing & Invoice History', starter: 'Basic', growth: 'Detailed', enterprise: 'Executive View' },
        { name: 'Participant Alerts & Reminders', starter: false, growth: true, enterprise: 'AI Risk Alerts' },
        { name: 'Enhanced UI & Structured Layout', starter: 'Basic', growth: 'Optimised', enterprise: 'Fully Custom UI' },
      ]
    },
    {
      id: 'security',
      title: 'Security & Compliance',
      icon: Shield,
      color: 'primary-navy',
      allPlans: true,
      features: [
        { name: 'Role-Based Access Control (RBAC)', starter: true, growth: true, enterprise: true },
        { name: 'Data Encryption', starter: true, growth: true, enterprise: true },
        { name: 'Secure Login Authentication', starter: true, growth: true, enterprise: true },
        { name: 'Audit Logs & Version Tracking', starter: true, growth: true, enterprise: true },
        { name: 'NDIS Privacy Compliance', starter: true, growth: true, enterprise: true },
      ]
    },
    {
      id: 'progress',
      title: 'Progress Notes',
      icon: FileText,
      color: 'tech-blue',
      features: [
        { name: 'Auto-Generated Title (Name + Date + Staff)', starter: true, growth: true, enterprise: true },
        { name: 'Timestamped Entries', starter: true, growth: true, enterprise: true },
        { name: 'Staff Auto-Fill', starter: true, growth: true, enterprise: true },
        { name: 'Free-Text Progress Details', starter: true, growth: true, enterprise: true },
        { name: 'Print Progress Notes', starter: true, growth: true, enterprise: true },
        { name: 'Export to PDF', starter: true, growth: true, enterprise: true },
        { name: 'Email Notifications', starter: false, growth: true, enterprise: 'Role-Based' },
        { name: 'Custom Notification Controls', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Audit Logs & Version Tracking', starter: false, growth: true, enterprise: true },
      ]
    },
    {
      id: 'charts',
      title: 'Charts & Tracking',
      icon: BarChart,
      color: 'care-teal',
      features: [
        { name: 'Visual Progress Charts', starter: 'Basic', growth: 'Advanced', enterprise: 'Advanced + Custom Views' },
        { name: 'Filter by Date / Provider', starter: false, growth: true, enterprise: true },
        { name: 'Export as PDF / Image', starter: false, growth: true, enterprise: true },
        { name: 'Automated Email Notifications', starter: false, growth: true, enterprise: 'Role-Based' },
        { name: 'Central Dashboard Alerts', starter: false, growth: false, enterprise: true },
        { name: 'Critical Case Tracking', starter: false, growth: false, enterprise: true },
        { name: 'Chart Links in Progress Notes', starter: false, growth: false, enterprise: true },
      ]
    },
    {
      id: 'careplan',
      title: 'Care Plan Management',
      icon: ClipboardList,
      color: 'tech-blue',
      features: [
        { name: 'Create & Edit Care Plans', starter: true, growth: true, enterprise: true },
        { name: 'Attach to Participant Profile', starter: true, growth: true, enterprise: true },
        { name: 'Real-Time Validation', starter: false, growth: true, enterprise: true },
        { name: 'Multiple Care Plans', starter: false, growth: true, enterprise: true },
        { name: 'Automatic Logging in Progress Notes', starter: false, growth: true, enterprise: true },
        { name: 'Version Control', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Overdue Care Plan Alerts', starter: false, growth: false, enterprise: true },
        { name: 'Email Distribution', starter: false, growth: true, enterprise: 'Automated' },
        { name: 'PDF Download', starter: true, growth: true, enterprise: true },
        { name: 'Compliance Validation Checklist', starter: false, growth: false, enterprise: true },
      ]
    },
    {
      id: 'assessments',
      title: 'Assessments Management',
      icon: FileText,
      color: 'care-teal',
      features: [
        { name: 'Functional Capacity Assessments', starter: true, growth: true, enterprise: true },
        { name: 'Risk Assessments', starter: true, growth: true, enterprise: true },
        { name: 'Support Needs Evaluations', starter: true, growth: true, enterprise: true },
        { name: 'Progress Reviews', starter: false, growth: true, enterprise: true },
        { name: 'Historical Comparison', starter: false, growth: true, enterprise: true },
        { name: 'Approval Workflow', starter: false, growth: false, enterprise: true },
        { name: 'Status Tracking', starter: false, growth: true, enterprise: true },
        { name: 'Compliance Checklist', starter: false, growth: false, enterprise: true },
        { name: 'Assessment-Based Reporting', starter: false, growth: false, enterprise: 'AI Reporting' },
        { name: 'Care Plan Integration', starter: false, growth: true, enterprise: 'Fully Integrated' },
      ]
    },
    {
      id: 'complaints',
      title: 'Complaints & Risk Management',
      icon: AlertTriangle,
      color: 'heritage-orange',
      features: [
        { name: 'Incident Logging', starter: true, growth: true, enterprise: true },
        { name: 'Unique Reference Codes', starter: true, growth: true, enterprise: true },
        { name: 'Custom Form Builder', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'WHS & Compliance Forms', starter: false, growth: true, enterprise: true },
        { name: 'Complaint Resolution Workflow', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Compliance Tracking Dashboard', starter: false, growth: false, enterprise: true },
        { name: 'Version Control', starter: false, growth: false, enterprise: true },
        { name: 'Audit Logs', starter: false, growth: false, enterprise: true },
        { name: 'Secure Storage & Encryption', starter: true, growth: true, enterprise: true },
      ]
    },
    {
      id: 'tasks',
      title: 'NDIS Support Task Management',
      icon: ClipboardList,
      color: 'tech-blue',
      features: [
        { name: 'Create Tasks Linked to Participant', starter: true, growth: true, enterprise: true },
        { name: 'Assign Task to Support Worker', starter: true, growth: true, enterprise: true },
        { name: 'Task Categories', starter: 'Basic', growth: 'Advanced', enterprise: 'Custom Templates' },
        { name: 'Scheduled / Recurring Tasks', starter: 'Basic', growth: 'Advanced', enterprise: 'Advanced' },
        { name: 'Task Notes & Observations', starter: true, growth: true, enterprise: true },
        { name: 'Status Tracking', starter: true, growth: true, enterprise: true },
        { name: 'Attach Photos / Documents', starter: false, growth: true, enterprise: true },
        { name: 'Link Task to Care Plan Goals', starter: false, growth: true, enterprise: 'Advanced Mapping' },
        { name: 'Auto-Generate Progress Note from Task', starter: false, growth: false, enterprise: true },
      ]
    },
    {
      id: 'siltasks',
      title: 'SIL & House-Based Tasks',
      icon: Home,
      color: 'care-teal',
      features: [
        { name: 'Create Tasks by SIL House', starter: 'Basic', growth: 'Advanced', enterprise: 'Multi-Site' },
        { name: 'Shared House Tasks', starter: false, growth: true, enterprise: true },
        { name: 'Room-Based Task Allocation', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Night Shift / Welfare Checks', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'House Compliance Checklists', starter: false, growth: true, enterprise: 'Automated' },
        { name: 'Critical Alerts', starter: false, growth: false, enterprise: 'AI Alerts' },
        { name: 'House Task Reports', starter: false, growth: true, enterprise: 'Executive Dashboard' },
      ]
    },
    {
      id: 'stafftasks',
      title: 'Staff & Role-Based Tasks',
      icon: Users,
      color: 'tech-blue',
      features: [
        { name: 'Role-Based Task Assignment', starter: 'Basic', growth: 'Advanced', enterprise: 'Skill Matching' },
        { name: 'Staff Task Dashboard', starter: true, growth: true, enterprise: true },
        { name: 'Shift-Based Task Allocation', starter: false, growth: true, enterprise: 'Automated' },
        { name: 'Completion Time Tracking', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Escalation for Overdue Tasks', starter: false, growth: false, enterprise: 'Automated' },
        { name: 'Multi-Level Task Approval', starter: false, growth: false, enterprise: true },
        { name: 'Staff Performance Metrics', starter: false, growth: false, enterprise: 'AI Insights' },
      ]
    },
    {
      id: 'medication',
      title: 'Medication & Clinical Support Tasks',
      icon: Heart,
      color: 'care-teal',
      features: [
        { name: 'Medication Reminder Tasks', starter: 'Basic', growth: 'Advanced', enterprise: 'Automated' },
        { name: 'Digital Medication Sign-Off', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Missed Medication Alerts', starter: false, growth: true, enterprise: 'AI Detection' },
        { name: 'Clinical Task Integration (eMAR)', starter: false, growth: true, enterprise: 'Full Integration' },
        { name: 'Escalation for Critical Missed Tasks', starter: false, growth: false, enterprise: 'Automated' },
      ]
    },
    {
      id: 'notifications',
      title: 'Notifications & Alerts',
      icon: Bell,
      color: 'tech-blue',
      features: [
        { name: 'Staff Task Notifications', starter: 'Basic', growth: 'Push', enterprise: 'Advanced' },
        { name: 'Overdue Task Alerts', starter: false, growth: true, enterprise: 'Automated' },
        { name: 'Case Manager Notifications', starter: false, growth: true, enterprise: 'Role-Based' },
        { name: 'Family Alerts (Controlled Access)', starter: false, growth: false, enterprise: true },
        { name: 'High-Risk Participant Alerts', starter: false, growth: false, enterprise: 'AI Monitoring' },
      ]
    },
    {
      id: 'appointments',
      title: 'Appointment Management',
      icon: Calendar,
      color: 'care-teal',
      features: [
        { name: 'Create Appointments', starter: true, growth: true, enterprise: true },
        { name: 'Assign Participant', starter: true, growth: true, enterprise: true },
        { name: 'Assign Staff / Provider', starter: true, growth: true, enterprise: true },
        { name: 'Appointment Type', starter: 'Basic', growth: 'Advanced', enterprise: 'Custom Templates' },
        { name: 'Reschedule / Cancel', starter: true, growth: true, enterprise: true },
        { name: 'Recurring Appointments', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Multi-Participant (Group Supports)', starter: false, growth: true, enterprise: true },
        { name: 'Location Management', starter: 'Basic', growth: 'Advanced', enterprise: 'Multi-Site' },
        { name: 'Email Reminder', starter: 'Basic', growth: true, enterprise: 'Advanced Rules' },
        { name: 'SMS Reminder', starter: 'Add-On', growth: 'Add-On', enterprise: 'Included' },
        { name: 'Staff Push Notification', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'No-Show Alerts', starter: false, growth: false, enterprise: 'Automated Escalation' },
        { name: 'Auto-Generate Progress Note Template', starter: false, growth: true, enterprise: 'Automated' },
        { name: 'Auto-Trigger Invoice', starter: false, growth: true, enterprise: 'Fully Automated' },
        { name: 'Conflict Detection', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Funding Validation', starter: 'Basic', growth: 'Advanced', enterprise: 'AI Validation' },
      ]
    },
    {
      id: 'documents',
      title: 'Document Management',
      icon: FolderOpen,
      color: 'tech-blue',
      features: [
        { name: 'Central Repository', starter: true, growth: true, enterprise: true },
        { name: 'Drag & Drop Upload', starter: true, growth: true, enterprise: true },
        { name: 'Bulk Upload', starter: false, growth: true, enterprise: true },
        { name: 'Version Tracking', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Tagging & Search Filters', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Document Expiry Alerts', starter: false, growth: true, enterprise: 'Automated' },
        { name: 'Multi-Factor Authentication', starter: false, growth: false, enterprise: true },
        { name: 'Audit Logs', starter: false, growth: false, enterprise: true },
        { name: 'Cloud Backup', starter: true, growth: true, enterprise: 'Dedicated Backup' },
      ]
    },
    {
      id: 'emar',
      title: 'Medication & Clinical (eMAR)',
      icon: Heart,
      color: 'care-teal',
      features: [
        { name: 'eMAR', starter: 'Basic', growth: 'Advanced', enterprise: 'Advanced + Alerts' },
        { name: 'Medication Scheduling', starter: true, growth: true, enterprise: true },
        { name: 'Medication Sign-Off', starter: true, growth: true, enterprise: true },
        { name: 'Missed Dose Alerts', starter: false, growth: true, enterprise: true },
        { name: 'AI Medication Pattern Detection', starter: false, growth: false, enterprise: true },
        { name: 'GP / Pharmacy Integration', starter: false, growth: 'Add-On', enterprise: 'Custom Integration' },
      ]
    },
    {
      id: 'crm',
      title: 'Participant CRM Integration',
      icon: Briefcase,
      color: 'tech-blue',
      features: [
        { name: 'Lead Management Module', starter: false, growth: true, enterprise: true },
        { name: 'Opportunity Stages', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Interaction Tracking', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Automated Follow-Ups', starter: false, growth: false, enterprise: true },
        { name: 'Lead Conversion Reporting', starter: false, growth: false, enterprise: true },
        { name: 'CRM → Participant Conversion', starter: false, growth: true, enterprise: 'Fully Automated' },
      ]
    },
    {
      id: 'rostering',
      title: 'Rostering & Workforce',
      icon: Calendar,
      color: 'care-teal',
      features: [
        { name: 'Create Shifts (Individual Participants)', starter: true, growth: true, enterprise: true },
        { name: 'Assign Staff to Participant', starter: true, growth: true, enterprise: true },
        { name: 'Recurring Shifts', starter: true, growth: true, enterprise: true },
        { name: 'Link Shift to Funding Category', starter: 'Basic', growth: 'Advanced', enterprise: 'AI Validation' },
        { name: 'Conflict Detection', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Shift History', starter: 'Basic', growth: 'Advanced', enterprise: 'Full Analytics' },
        { name: 'Auto Invoice Trigger', starter: false, growth: true, enterprise: 'Fully Automated' },
        { name: 'Risk Alerts for Missed Support', starter: false, growth: false, enterprise: 'AI Monitoring' },
        { name: 'Create Roster by SIL House', starter: 'Basic', growth: 'Advanced', enterprise: 'Multi-Site' },
        { name: 'Multi-Participant per Shift', starter: false, growth: true, enterprise: true },
        { name: 'Night/Sleepover Shifts', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Vacancy & Coverage Alerts', starter: false, growth: false, enterprise: 'Predictive' },
        { name: 'Skill & Qualification Matching', starter: false, growth: true, enterprise: 'AI Matching' },
        { name: 'Award & Overtime Rule Validation', starter: false, growth: true, enterprise: 'Advanced Compliance' },
        { name: 'Overtime Warning Alerts', starter: false, growth: false, enterprise: 'AI Alerts' },
        { name: 'Performance-Based Allocation', starter: false, growth: false, enterprise: 'AI Recommendation' },
        { name: 'Multi-Site Staff Pooling', starter: false, growth: false, enterprise: true },
      ]
    },
    {
      id: 'workforce',
      title: 'Workforce Summary',
      icon: Users,
      color: 'tech-blue',
      features: [
        { name: 'Secure Staff Login', starter: true, growth: true, enterprise: 'Advanced Security' },
        { name: '2FA Authentication', starter: true, growth: true, enterprise: true },
        { name: 'Staff Role Permissions', starter: 'Basic', growth: 'Advanced', enterprise: 'Enterprise RBAC' },
        { name: 'Staff Roster', starter: 'Basic', growth: 'Smart Roster', enterprise: 'AI Predictive Roster' },
        { name: 'Leave Management', starter: false, growth: true, enterprise: true },
        { name: 'Payslip Access', starter: false, growth: true, enterprise: true },
        { name: 'Workforce Analytics', starter: false, growth: false, enterprise: true },
        { name: 'Predictive Staffing', starter: false, growth: false, enterprise: true },
        { name: 'Compliance Shift Alerts', starter: false, growth: true, enterprise: 'AI Alerts' },
      ]
    },
    {
      id: 'invoicing',
      title: 'NDIS Invoicing & Claims',
      icon: Wallet,
      color: 'care-teal',
      features: [
        { name: 'Invoice from Completed Shifts', starter: true, growth: true, enterprise: true },
        { name: 'Manual Invoice Creation', starter: true, growth: true, enterprise: true },
        { name: 'NDIS Claim Generation', starter: true, growth: true, enterprise: true },
        { name: 'Claim/Invoice List Views', starter: true, growth: true, enterprise: true },
        { name: 'Void/Cancel Claims', starter: true, growth: true, enterprise: true },
        { name: 'Void Invoice Tracking', starter: 'Basic', growth: 'Advanced', enterprise: 'Full Audit History' },
        { name: 'Claim Status Tracking', starter: false, growth: true, enterprise: true },
        { name: 'Funding Category Validation', starter: 'Basic', growth: 'Advanced', enterprise: 'AI Validation' },
        { name: 'Bulk Claim Generation', starter: false, growth: true, enterprise: true },
        { name: 'Bulk PRODA Upload via API', starter: false, growth: true, enterprise: true },
        { name: 'Auto-Generate Claims (Approved Quotes)', starter: false, growth: true, enterprise: true },
        { name: 'Automatic Error Detection', starter: false, growth: false, enterprise: true },
        { name: 'Rejected Claim Alerts', starter: false, growth: false, enterprise: true },
        { name: 'Resubmission Workflow', starter: false, growth: false, enterprise: true },
        { name: 'Multi-Participant Batch Claims', starter: false, growth: false, enterprise: true },
      ]
    },
    {
      id: 'quotes',
      title: 'Quote Management',
      icon: FileText,
      color: 'tech-blue',
      features: [
        { name: 'Create New Quote', starter: true, growth: true, enterprise: true },
        { name: 'Quote List View', starter: true, growth: true, enterprise: true },
        { name: 'Edit/Update Quotes', starter: true, growth: true, enterprise: true },
        { name: 'Duplicate Quote', starter: false, growth: true, enterprise: true },
        { name: 'Archive Quotes', starter: true, growth: true, enterprise: true },
        { name: 'Convert Quote → Service Agreement', starter: false, growth: true, enterprise: true },
        { name: 'Add Line Items', starter: true, growth: true, enterprise: true },
        { name: 'GST Handling', starter: true, growth: true, enterprise: true },
        { name: 'Funding Category Allocation', starter: 'Basic', growth: 'Advanced', enterprise: 'Advanced + Validation' },
        { name: 'Quote Status Tracking', starter: false, growth: true, enterprise: true },
        { name: 'Digital Approval Tracking', starter: false, growth: true, enterprise: true },
        { name: 'Email Quote', starter: false, growth: true, enterprise: true },
        { name: 'Follow-Up Reminders', starter: false, growth: false, enterprise: true },
        { name: 'Multi-Version Quotes', starter: false, growth: false, enterprise: true },
        { name: 'Internal Approval Workflow', starter: false, growth: false, enterprise: true },
      ]
    },
    {
      id: 'property',
      title: 'Property, Assets & House Management',
      icon: Home,
      color: 'care-teal',
      features: [
        { name: 'Manage SIL Houses', starter: 'Basic', growth: 'Advanced', enterprise: 'Multi-Site Advanced' },
        { name: 'Assign Participants/Staff to Houses', starter: true, growth: true, enterprise: true },
        { name: 'Occupancy Tracking', starter: 'Basic', growth: 'Advanced', enterprise: 'Real-Time Dashboard' },
        { name: 'Room Allocation', starter: false, growth: true, enterprise: true },
        { name: 'Vacancy Monitoring', starter: false, growth: true, enterprise: 'Predictive Alerts' },
        { name: 'House Compliance Checklist', starter: 'Basic', growth: 'Advanced', enterprise: 'Automated Compliance' },
        { name: 'House Incident Tracking', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Multi-State House Management', starter: false, growth: false, enterprise: true },
        { name: 'Asset Register (Basic List)', starter: true, growth: true, enterprise: true },
        { name: 'Asset Category Classification', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Asset Location Tracking', starter: 'Basic', growth: 'Advanced', enterprise: 'Multi-Site' },
        { name: 'Maintenance Schedule Tracking', starter: false, growth: true, enterprise: 'Automated Alerts' },
        { name: 'Warranty & Expiry Tracking', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Asset Depreciation Tracking', starter: false, growth: false, enterprise: true },
        { name: 'QR/Barcode Tracking', starter: false, growth: false, enterprise: true },
      ]
    },
    {
      id: 'employee',
      title: 'Employee Dashboard & HR',
      icon: Users,
      color: 'tech-blue',
      features: [
        { name: 'Rosters & Upcoming Shifts', starter: true, growth: true, enterprise: true },
        { name: 'Real-Time Shift Notifications', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Payslips Access', starter: true, growth: true, enterprise: true },
        { name: 'Tax Summaries', starter: false, growth: true, enterprise: true },
        { name: 'Update Personal Details', starter: false, growth: true, enterprise: 'Advanced Validation' },
        { name: 'Credential Expiry Tracking', starter: false, growth: true, enterprise: 'Automated Alerts' },
        { name: 'Upload Renewed Credentials', starter: false, growth: true, enterprise: 'Workflow-Based' },
        { name: 'Apply Leave via Mobile', starter: true, growth: true, enterprise: true },
        { name: 'Leave Balances', starter: true, growth: true, enterprise: true },
        { name: 'Manager Approval via Mobile', starter: false, growth: true, enterprise: 'Advanced Workflow' },
        { name: 'Leave Conflict Detection', starter: false, growth: false, enterprise: true },
        { name: 'Submit Timesheet via Mobile/Web', starter: true, growth: true, enterprise: true },
        { name: 'Pre-Filled Roster Entries', starter: false, growth: true, enterprise: true },
        { name: 'Payroll Sync', starter: 'Add-On', growth: true, enterprise: 'Advanced API' },
        { name: 'Manager Approval Workflow', starter: false, growth: true, enterprise: 'Multi-Level' },
        { name: 'GPS-Based Verification', starter: false, growth: false, enterprise: true },
      ]
    },
    {
      id: 'recruitment',
      title: 'HR & Recruitment',
      icon: Briefcase,
      color: 'care-teal',
      features: [
        { name: 'Create & Publish Job Listings', starter: true, growth: true, enterprise: true },
        { name: 'Application Forms', starter: 'Basic', growth: 'Advanced', enterprise: 'Advanced + Conditional Logic' },
        { name: 'Career Page Integration', starter: 'Basic', growth: 'Branded Page', enterprise: 'Fully Custom Careers Portal' },
        { name: 'Application Tracking Dashboard', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Job Board Integration', starter: false, growth: 'Add-On', enterprise: 'API Integration' },
        { name: 'Candidate Database', starter: 'Basic', growth: 'Advanced Search', enterprise: 'Advanced + Filters' },
        { name: 'Resume Parsing', starter: false, growth: true, enterprise: 'AI Enhanced' },
        { name: 'Interview Scheduling', starter: false, growth: true, enterprise: 'Calendar Integration' },
        { name: 'Candidate Notes & Ratings', starter: false, growth: true, enterprise: 'Advanced Scoring' },
        { name: 'Pipeline Stages', starter: false, growth: true, enterprise: 'Custom Stages' },
        { name: 'Talent Pool', starter: false, growth: false, enterprise: true },
        { name: 'Recruitment KPI Reporting', starter: false, growth: false, enterprise: 'Executive Dashboard' },
      ]
    },
    {
      id: 'onboarding',
      title: 'Onboarding Management',
      icon: UserCircle,
      color: 'tech-blue',
      features: [
        { name: 'Digital Onboarding Checklist', starter: 'Basic', growth: 'Advanced', enterprise: 'Customisable' },
        { name: 'Document Collection', starter: 'Basic Upload', growth: 'Secure Portal', enterprise: 'Automated Workflow' },
        { name: 'Police Check Tracking', starter: false, growth: true, enterprise: 'Automated Expiry Alerts' },
        { name: 'WWCC Tracking', starter: false, growth: true, enterprise: 'Automated Renewal Reminders' },
        { name: 'Induction Training', starter: false, growth: true, enterprise: 'LMS Integration' },
        { name: 'Contract Signing', starter: false, growth: true, enterprise: 'Digital E-Signature' },
        { name: 'Credential Verification', starter: false, growth: false, enterprise: 'Automated' },
        { name: 'Onboarding Progress Tracking', starter: false, growth: false, enterprise: 'Dashboard View' },
      ]
    },
    {
      id: 'lms',
      title: 'eLearning & Training Module (LMS)',
      icon: GraduationCap,
      color: 'care-teal',
      features: [
        { name: 'Create & Manage Courses', starter: true, growth: true, enterprise: true },
        { name: 'Upload Video/PDF', starter: true, growth: true, enterprise: true },
        { name: 'Interactive Modules', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'SCORM/xAPI', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Course Categories', starter: 'Basic', growth: 'Advanced', enterprise: 'Custom Structure' },
        { name: 'Course Version Control', starter: false, growth: true, enterprise: 'Full History' },
        { name: 'Expiry-Based Courses', starter: false, growth: true, enterprise: 'Automated Alerts' },
        { name: 'Multi-Site Course Access', starter: false, growth: false, enterprise: true },
        { name: 'Role-Based Learning Paths', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Mandatory Compliance Tracks', starter: false, growth: true, enterprise: 'Automated Enforcement' },
        { name: 'Auto-Assign by Role', starter: false, growth: false, enterprise: true },
        { name: 'Recurring Training', starter: false, growth: false, enterprise: 'Automated' },
        { name: 'Individual Progress', starter: 'Basic', growth: 'Advanced', enterprise: 'Advanced' },
        { name: 'Completion Reports', starter: false, growth: true, enterprise: 'Automated' },
        { name: 'Compliance Gap Analysis', starter: false, growth: true, enterprise: 'AI-Driven' },
        { name: 'Expiring Certification Alerts', starter: false, growth: true, enterprise: 'Automated SMS/Email' },
        { name: 'Team/Dept Analytics', starter: false, growth: false, enterprise: 'Executive Dashboard' },
        { name: 'Workforce Compliance Score', starter: false, growth: false, enterprise: 'AI Scorecard' },
      ]
    },
    {
      id: 'ai',
      title: 'AI, Compliance & Business Intelligence',
      icon: Brain,
      color: 'purple-600',
      features: [
        { name: 'AI Compliance Monitoring', starter: false, growth: true, enterprise: 'Advanced' },
        { name: 'Real-Time Compliance Alerts', starter: false, growth: false, enterprise: true },
        { name: 'AI Risk Flags', starter: false, growth: false, enterprise: true },
        { name: 'Audit-Ready Reports', starter: 'Basic', growth: 'Advanced', enterprise: 'Automated Audit Reports' },
        { name: 'CRM (Lead → Participant)', starter: false, growth: true, enterprise: true },
        { name: 'Custom Workflow Approvals', starter: false, growth: false, enterprise: true },
        { name: 'AI Operational Insights', starter: false, growth: false, enterprise: true },
        { name: 'Executive KPI Dashboard', starter: false, growth: false, enterprise: true },
      ]
    },
    {
      id: 'integrations',
      title: 'Integrations & Infrastructure',
      icon: Settings,
      color: 'tech-blue',
      features: [
        { name: 'API Integrations', starter: 'Limited', growth: 'Standard', enterprise: 'Custom API' },
        { name: 'Xero / MYOB', starter: 'Add-On', growth: true, enterprise: 'Advanced Sync' },
        { name: 'Stripe Integration', starter: 'Add-On', growth: true, enterprise: true },
        { name: 'Payroll Sync', starter: false, growth: 'Optional', enterprise: true },
        { name: 'Hosting', starter: 'Optional', growth: 'Optional', enterprise: 'Dedicated Architecture' },
        { name: 'Advanced Security Firewall', starter: false, growth: true, enterprise: true },
        { name: 'Data Backup & Recovery', starter: true, growth: true, enterprise: 'Enterprise Level' },
        { name: 'Dedicated Account Manager', starter: false, growth: false, enterprise: true },
        { name: 'Onsite Training (Australia)', starter: false, growth: false, enterprise: true },
        { name: 'Support Level', starter: 'Standard', growth: 'Priority', enterprise: 'VIP Dedicated' },
      ]
    },
  ];

  // Use CMS sections or fallback to defaults
  const featureSections = cmsSections.length > 0
    ? cmsSections.map(sec => ({
        id: sec.slug,
        title: sec.title,
        icon: getIconComponent(sec.icon),
        color: sec.color,
        allPlans: sec.all_plans || false,
        features: sec.features || []
      }))
    : defaultFeatureSections;

  // Default pricing plans
  const defaultPricingPlans = [
    {
      id: 'starter',
      name: 'STARTER',
      icon: Rocket,
      color: 'care-teal',
      gradient: 'from-care-teal to-emerald-500',
      buildPrice: '$18,500',
      hostingPrice: '$390',
      description: 'Perfect for small NDIS providers getting started',
      features: ['Full Platform Ownership', 'Branded Web + Mobile', 'Core NDIS Modules', 'Standard Support'],
      popular: false
    },
    {
      id: 'growth',
      name: 'GROWTH',
      icon: TrendingUp,
      color: 'tech-blue',
      gradient: 'from-tech-blue to-blue-500',
      buildPrice: '$32,000',
      hostingPrice: '$690',
      description: 'For growing providers needing advanced features',
      features: ['Everything in Starter', 'Advanced Rostering', 'AI Compliance Tools', 'Priority Support', 'CRM Integration'],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'ENTERPRISE',
      icon: Crown,
      color: 'purple-600',
      gradient: 'from-purple-600 to-purple-500',
      buildPrice: 'Custom',
      hostingPrice: 'Custom',
      description: 'For large organisations with complex needs',
      features: ['Everything in Growth', 'Full AI Suite', 'Multi-Site Support', 'Dedicated Manager', 'Custom Integrations'],
      popular: false
    }
  ];

  // Use CMS packages or fallback to defaults
  const pricingPlans = cmsPackages.length > 0
    ? cmsPackages.map(pkg => ({
        id: pkg.slug,
        name: pkg.name,
        icon: getIconComponent(pkg.icon),
        color: pkg.color,
        gradient: pkg.gradient || `from-${pkg.color} to-${pkg.color}`,
        buildPrice: pkg.build_price,
        hostingPrice: pkg.hosting_price,
        description: pkg.description,
        features: pkg.features || [],
        popular: pkg.is_popular
      }))
    : defaultPricingPlans;

  // Stats data
  // Use CMS trust badges from merged settings
  const trustBadges = settings.trust_badges;

  const renderFeatureValue = (value, tier) => {
    if (value === true) {
      return <Check className="text-care-teal mx-auto" size={18} />;
    }
    if (value === false) {
      return <X className="text-gray-300 mx-auto" size={18} />;
    }
    return <span className={`text-xs font-medium ${tier === 'enterprise' ? 'text-purple-600' : tier === 'growth' ? 'text-tech-blue' : 'text-gray-600'}`}>{value}</span>;
  };

  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title="Features & Pricing | GetCare 360 - NDIS Software Australia"
        description="Explore GetCare 360's comprehensive feature set and transparent pricing. One-time investment, no per-user fees. Build your own custom NDIS platform."
        keywords="NDIS software pricing, NDIS provider features, disability care software cost, NDIS platform pricing Australia"
        canonicalUrl="/features-pricing"
        page="features-pricing"
      />
      <Toaster position="top-right" richColors />
      <Header />

      {/* Hero Section - Redesigned */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary-navy via-[#0d2847] to-[#0a1f3d] relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-care-teal/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }}></div>
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-tech-blue/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s', animationDelay: '1s' }}></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
              <Sparkles className="text-care-teal mr-2" size={16} />
              <span className="text-sm font-medium text-white/90">{settings.hero_badge}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              <span className="block">{settings.hero_title}</span>
              <span className="block mt-2 bg-gradient-to-r from-care-teal via-tech-blue to-purple-400 bg-clip-text text-transparent">
                Features & Pricing
              </span>
            </h1>
            
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
              {settings.hero_subtitle}
            </p>

            {/* Tier Pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {pricingPlans.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => setActiveTier(plan.id)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    activeTier === plan.id
                      ? `bg-gradient-to-r ${plan.gradient} text-white shadow-lg scale-105`
                      : 'bg-white/10 text-white/80 hover:bg-white/20'
                  }`}
                >
                  {plan.name}
                </button>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openBooking}
                className="bg-care-teal hover:bg-care-teal/90 text-white px-8 py-6 text-lg group shadow-lg shadow-care-teal/30 hover:shadow-care-teal/50 transition-all"
              >
                <Play className="mr-2 group-hover:scale-110 transition-transform" size={20} />
                Book a Demo
              </Button>
              <Button 
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                variant="outline" 
                className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                View Pricing
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {settings.stats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/10 hover:border-care-teal/30 transition-all duration-300 group"
              >
                <div className="text-3xl md:text-4xl font-bold text-white mb-1 group-hover:text-care-teal transition-colors">
                  <AnimatedNumber value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-white/60">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Feature Explorer */}
      <section className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
              {settings.explorer_title.includes('Features') ? (
                <>
                  {settings.explorer_title.split('Features')[0]}
                  <span className="text-tech-blue">Features</span>
                  {settings.explorer_title.split('Features')[1]}
                </>
              ) : settings.explorer_title}
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {settings.explorer_subtitle}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {featureCategories.map((cat) => (
              <CategoryTab
                key={cat.id}
                icon={cat.icon}
                label={cat.label}
                isActive={activeCategory === cat.id}
                onClick={() => setActiveCategory(cat.id)}
                color={cat.color}
              />
            ))}
          </div>

          {/* Tier Selector */}
          <div className="flex justify-center gap-4 mb-8">
            {pricingPlans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setActiveTier(plan.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeTier === plan.id
                    ? `bg-${plan.color} text-white shadow-md`
                    : 'bg-white text-text-secondary border border-border-line hover:border-gray-300'
                }`}
              >
                {plan.name}
              </button>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {featuresData[activeCategory]?.map((feature, index) => (
              <FeatureCard 
                key={feature.name} 
                feature={feature} 
                tier={activeTier}
                delay={index * 50}
              />
            ))}
          </div>

          {/* View All Features Button */}
          <div className="text-center mt-8">
            <Button 
              onClick={() => document.getElementById('all-features').scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              className="border-tech-blue text-tech-blue hover:bg-tech-blue hover:text-white"
            >
              View Complete Feature List
              <ChevronDown className="ml-2" size={16} />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Cards Section - Redesigned Investment Model */}
      {settings.show_pricing_packages && (
      <section id="pricing" className="py-24 relative overflow-hidden">
        {/* Light background when unlocked, dark when locked */}
        <div className={`absolute inset-0 transition-colors duration-500 ${pricingUnlocked ? 'bg-gradient-to-b from-gray-50 to-white' : 'bg-gradient-to-b from-primary-navy via-[#0d2847] to-[#0a1f3d]'}`}></div>
        
        {/* Animated Background - only show when locked */}
        {!pricingUnlocked && (
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-[400px] h-[400px] bg-care-teal/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }}></div>
            <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-tech-blue/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s', animationDelay: '2s' }}></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl"></div>
            
            {/* Grid Pattern */}
            <div className="absolute inset-0 opacity-[0.02]" style={{
              backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}></div>
          </div>
        )}
        
        {/* Subtle pattern for unlocked state */}
        {pricingUnlocked && (
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-tech-blue/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-care-teal/5 rounded-full blur-3xl"></div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className={`inline-flex items-center px-4 py-2 rounded-full border mb-6 ${
              pricingUnlocked 
                ? 'bg-tech-blue/10 border-tech-blue/20' 
                : 'bg-white/10 backdrop-blur-sm border-white/20'
            }`}>
              <Sparkles className="text-care-teal mr-2" size={16} />
              <span className={`text-sm font-medium ${pricingUnlocked ? 'text-primary-navy' : 'text-white/90'}`}>{settings.pricing_badge}</span>
            </div>
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 ${pricingUnlocked ? 'text-primary-navy' : 'text-white'}`}>
              {settings.pricing_title.includes('Investment') ? (
                <>
                  {settings.pricing_title.split('Investment')[0]}
                  <span className="bg-gradient-to-r from-care-teal to-tech-blue bg-clip-text text-transparent">Investment</span>
                  {settings.pricing_title.split('Investment')[1]}
                </>
              ) : settings.pricing_title}
            </h2>
            <p className={`text-xl max-w-2xl mx-auto ${pricingUnlocked ? 'text-text-secondary' : 'text-white/70'}`}>
              {settings.pricing_subtitle}
            </p>
          </div>

          {/* Gated Pricing */}
          {!pricingUnlocked ? (
            <div className="max-w-3xl mx-auto">
              <div className="relative">
                {/* Glassmorphism Card */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-8 md:p-12 text-center relative overflow-hidden">
                  {/* Animated Gradient Border */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-care-teal via-tech-blue to-purple-500 opacity-20 animate-pulse" style={{ animationDuration: '4s' }}></div>
                  
                  <div className="relative z-10">
                    {/* Lock Icon with Pulse */}
                    <div className="relative inline-flex mb-8">
                      <div className="w-24 h-24 bg-gradient-to-br from-tech-blue to-purple-600 rounded-full flex items-center justify-center shadow-2xl shadow-tech-blue/30">
                        <Lock className="text-white" size={40} />
                      </div>
                      <div className="absolute inset-0 w-24 h-24 bg-gradient-to-br from-tech-blue to-purple-600 rounded-full animate-ping opacity-20"></div>
                    </div>
                    
                    <h3 className="text-3xl font-bold text-white mb-4">
                      Unlock Investment Details
                    </h3>
                    <p className="text-white/70 mb-10 max-w-lg mx-auto text-lg">
                      Enter your details to see complete pricing packages with build investment and hosting costs.
                    </p>
                    
                    {/* Preview Tier Cards */}
                    <div className="grid grid-cols-3 gap-4 mb-10">
                      {pricingPlans.map((plan, index) => {
                        const Icon = plan.icon;
                        return (
                          <div 
                            key={plan.id}
                            className={`group p-5 rounded-2xl transition-all duration-500 hover:scale-105 cursor-pointer ${
                              plan.popular 
                                ? 'bg-gradient-to-br from-tech-blue/30 to-purple-600/30 border-2 border-tech-blue/50' 
                                : 'bg-white/5 border border-white/10 hover:bg-white/10'
                            }`}
                          >
                            <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                              plan.popular ? 'bg-white/20' : 'bg-white/10'
                            }`}>
                              <Icon className="text-white" size={24} />
                            </div>
                            <p className="text-lg font-bold text-white">{plan.name}</p>
                            <p className="text-xs text-white/60 mt-1">
                              {index === 0 ? 'Small teams' : index === 1 ? 'Growing orgs' : 'Enterprise'}
                            </p>
                            {plan.popular && (
                              <span className="inline-flex items-center px-2 py-0.5 mt-2 text-[10px] font-bold bg-tech-blue text-white rounded-full">
                                POPULAR
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <Button 
                      onClick={handleUnlockPricing}
                      className="bg-gradient-to-r from-care-teal to-tech-blue hover:opacity-90 text-white px-10 py-7 text-lg shadow-2xl shadow-care-teal/30 hover:shadow-care-teal/50 transition-all group"
                      data-testid="unlock-pricing-btn"
                    >
                      <Eye className="mr-2 group-hover:scale-110 transition-transform" size={22} />
                      View Investment Packages
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={22} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Pricing Cards Grid - Clean design without overlay */}
              <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                {pricingPlans.map((plan, index) => {
                  const Icon = plan.icon;
                  const isSelected = activeTier === plan.id;
                  return (
                    <div
                      key={plan.id}
                      onClick={() => setActiveTier(plan.id)}
                      data-testid={`pricing-card-${plan.id}`}
                      className={`relative group cursor-pointer transition-all duration-300 ${
                        plan.popular ? 'md:-mt-6 md:mb-6' : ''
                      }`}
                    >
                      {/* Selected indicator ring */}
                      {isSelected && (
                        <div className={`absolute -inset-1 bg-gradient-to-r ${plan.gradient} rounded-3xl blur-sm opacity-60 animate-pulse`} style={{ animationDuration: '2s' }}></div>
                      )}
                      
                      {plan.popular && (
                        <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10">
                          <span className="bg-gradient-to-r from-tech-blue to-purple-600 text-white text-xs font-bold px-6 py-2 rounded-full shadow-lg shadow-tech-blue/30 flex items-center gap-1">
                            <Star className="fill-white" size={12} />
                            MOST POPULAR
                          </span>
                        </div>
                      )}
                      
                      <Card className={`relative h-full overflow-hidden transition-all duration-500 hover:-translate-y-2 ${
                        isSelected
                          ? `bg-gradient-to-b from-white to-${plan.color}/10 border-2 border-${plan.color} shadow-2xl`
                          : plan.popular 
                            ? 'bg-gradient-to-b from-white to-tech-blue/5 border-2 border-tech-blue shadow-xl shadow-tech-blue/20' 
                            : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-lg'
                      }`}>
                        {/* Selection Check Mark */}
                        {isSelected && (
                          <div className={`absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-to-r ${plan.gradient} flex items-center justify-center z-20 shadow-lg`}>
                            <Check className="text-white" size={18} />
                          </div>
                        )}
                        
                        {/* Gradient Header */}
                        <div className={`bg-gradient-to-r ${plan.gradient} p-6 text-white relative overflow-hidden`}>
                          {/* Decorative Elements */}
                          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                          
                          <div className="relative z-10">
                            <div className="flex items-center justify-between mb-3">
                              <div className={`w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center transition-transform ${isSelected ? 'scale-110' : ''}`}>
                                <Icon size={24} />
                              </div>
                              {plan.popular && <Star className="fill-white" size={20} />}
                            </div>
                            <h3 className="text-xl font-bold">{plan.name}</h3>
                            <p className="text-white/80 text-sm mt-1">{plan.description}</p>
                          </div>
                        </div>

                        <CardContent className="p-6">
                          {/* Build Investment */}
                          <div className="mb-4">
                            <p className="text-xs text-text-secondary uppercase tracking-wide mb-1">One-Time Build</p>
                            <div className="flex items-baseline gap-2">
                              <span className={`text-4xl font-bold ${isSelected ? `text-${plan.color}` : 'text-primary-navy'}`}>
                                {plan.buildPrice}
                              </span>
                              {plan.buildPrice !== 'Custom' && <span className="text-text-secondary">AUD</span>}
                            </div>
                          </div>
                          
                          {/* Hosting */}
                          <div className={`mb-6 p-3 rounded-xl border ${
                            isSelected 
                              ? `bg-${plan.color}/5 border-${plan.color}/20` 
                              : 'bg-gray-50 border-gray-100'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-[10px] text-text-secondary uppercase tracking-wide">Monthly Hosting</p>
                                <div className="flex items-baseline gap-1">
                                  <span className={`text-xl font-bold ${isSelected ? `text-${plan.color}` : 'text-care-teal'}`}>{plan.hostingPrice}</span>
                                  {plan.hostingPrice !== 'Custom' && <span className="text-text-secondary text-sm">/mo</span>}
                                </div>
                              </div>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                isSelected ? `bg-${plan.color}/10` : 'bg-care-teal/10'
                              }`}>
                                <Cloud className={isSelected ? `text-${plan.color}` : 'text-care-teal'} size={16} />
                              </div>
                            </div>
                          </div>

                          {/* Features */}
                          <ul className="space-y-2 mb-6">
                            {plan.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                  isSelected ? `bg-${plan.color}/20` : `bg-${plan.color}/10`
                                }`}>
                                  <Check className={`text-${plan.color}`} size={10} />
                                </div>
                                <span className="text-sm text-text-secondary">{feature}</span>
                              </li>
                            ))}
                          </ul>

                          {/* CTA */}
                          <Button 
                            onClick={(e) => { e.stopPropagation(); openBooking(); }}
                            data-testid={`book-demo-${plan.id}`}
                            className={`w-full py-5 text-base transition-all duration-300 group/btn ${
                              isSelected || plan.popular
                                ? `bg-gradient-to-r ${plan.gradient} text-white hover:opacity-90 shadow-lg hover:shadow-xl` 
                                : `bg-white text-primary-navy border-2 border-primary-navy hover:bg-primary-navy hover:text-white`
                            }`}
                          >
                            Book a Demo
                            <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" size={16} />
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Trust Badges */}
          <div className="mt-16 flex flex-wrap justify-center gap-6">
            {trustBadges.map((badge, index) => {
              const BadgeIcon = getIconComponent(badge.icon);
              return (
                <div 
                  key={index}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
                    pricingUnlocked 
                      ? 'bg-white border-gray-200 shadow-sm' 
                      : 'bg-white/5 backdrop-blur-sm border-white/10'
                  }`}
                >
                  <BadgeIcon size={16} className="text-care-teal" />
                  <span className={`text-sm ${pricingUnlocked ? 'text-text-secondary' : 'text-white/80'}`}>{badge.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Full Feature Comparison Accordion */}
      <section id="all-features" className="py-20 bg-page-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
              {settings.comparison_title.includes('Feature') ? (
                <>
                  {settings.comparison_title.split('Feature')[0]}
                  <span className="text-tech-blue">Feature</span>
                  {settings.comparison_title.split('Feature')[1]}
                </>
              ) : settings.comparison_title}
            </h2>
            <p className="text-text-secondary mb-6">{settings.comparison_subtitle || 'Click on each section to expand and view detailed features'}</p>
            
            {/* Search Box */}
            <div className="max-w-xl mx-auto relative">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
                <input
                  type="text"
                  placeholder="Search 300+ features... (e.g., rostering, compliance, invoice)"
                  value={searchQuery}
                  onChange={(e) => {
                    const query = e.target.value;
                    setSearchQuery(query);
                    
                    if (query.length >= 2) {
                      // Search through all feature sections
                      const results = [];
                      featureSections.forEach(section => {
                        const matchingFeatures = section.features.filter(f => 
                          f.name.toLowerCase().includes(query.toLowerCase())
                        );
                        if (matchingFeatures.length > 0) {
                          results.push({
                            section,
                            features: matchingFeatures
                          });
                        }
                      });
                      setSearchResults(results);
                      
                      // Auto-expand matching sections
                      const newExpanded = {};
                      results.forEach(r => {
                        newExpanded[r.section.id] = true;
                      });
                      setExpandedSections(newExpanded);
                    } else {
                      setSearchResults([]);
                    }
                  }}
                  className="w-full pl-12 pr-4 py-4 bg-white border-2 border-border-line rounded-xl text-text-primary placeholder:text-text-secondary/60 focus:border-tech-blue focus:outline-none focus:ring-2 focus:ring-tech-blue/20 transition-all"
                  data-testid="feature-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                      setExpandedSections({});
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors"
                  >
                    <X size={14} className="text-text-secondary" />
                  </button>
                )}
              </div>
              
              {/* Search Results Summary */}
              {searchQuery.length >= 2 && (
                <div className="mt-3 text-sm">
                  {searchResults.length > 0 ? (
                    <p className="text-care-teal font-medium">
                      Found {searchResults.reduce((acc, r) => acc + r.features.length, 0)} features in {searchResults.length} categories
                    </p>
                  ) : (
                    <p className="text-heritage-orange">No features found for "{searchQuery}"</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {(searchQuery.length >= 2 ? searchResults.map(r => r.section) : featureSections).map((section) => {
              const Icon = section.icon;
              const isExpanded = expandedSections[section.id];
              
              // Get features to display (filtered if searching)
              const featuresToShow = searchQuery.length >= 2 
                ? searchResults.find(r => r.section.id === section.id)?.features || []
                : section.features;
              
              // Highlight matching text
              const highlightMatch = (text) => {
                if (searchQuery.length < 2) return text;
                const regex = new RegExp(`(${searchQuery})`, 'gi');
                const parts = text.split(regex);
                return parts.map((part, i) => 
                  regex.test(part) ? (
                    <span key={i} className="bg-yellow-200 text-primary-navy font-semibold">{part}</span>
                  ) : part
                );
              };
              
              return (
                <div 
                  key={section.id} 
                  className={`bg-white rounded-xl border transition-all duration-300 overflow-hidden ${
                    isExpanded ? 'border-tech-blue/50 shadow-lg' : 'border-border-line hover:border-gray-300'
                  } ${searchQuery.length >= 2 && searchResults.find(r => r.section.id === section.id) ? 'ring-2 ring-care-teal/30' : ''}`}
                >
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(section.id)}
                    className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${
                      isExpanded ? 'bg-tech-blue/5' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-${section.color}/20 to-${section.color}/10 flex items-center justify-center transition-transform ${isExpanded ? 'scale-110' : ''}`}>
                        <Icon className={`text-${section.color}`} size={20} />
                      </div>
                      <span className={`font-semibold transition-colors ${isExpanded ? 'text-tech-blue' : 'text-primary-navy'}`}>
                        {section.title}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        searchQuery.length >= 2 && featuresToShow.length > 0
                          ? 'bg-care-teal/20 text-care-teal font-semibold'
                          : 'bg-gray-100 text-text-secondary'
                      }`}>
                        {searchQuery.length >= 2 ? `${featuresToShow.length} match${featuresToShow.length !== 1 ? 'es' : ''}` : `${section.features.length} features`}
                      </span>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isExpanded ? 'bg-tech-blue text-white rotate-180' : 'bg-gray-100 text-text-secondary'
                    }`}>
                      <ChevronDown size={18} />
                    </div>
                  </button>

                  {/* Feature Table */}
                  {isExpanded && (
                    <div className="border-t border-border-line overflow-x-auto animate-in slide-in-from-top-2 duration-300">
                      <table className="w-full min-w-[600px]">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-3 text-left text-sm font-semibold text-primary-navy w-1/2">Feature</th>
                            <th className="px-4 py-3 text-center w-1/6">
                              <span className="inline-flex items-center gap-1 text-care-teal text-sm font-semibold">
                                <Rocket size={14} />
                                Starter
                              </span>
                            </th>
                            <th className="px-4 py-3 text-center w-1/6">
                              <span className="inline-flex items-center gap-1 text-tech-blue text-sm font-semibold">
                                <TrendingUp size={14} />
                                Growth
                              </span>
                            </th>
                            <th className="px-4 py-3 text-center w-1/6">
                              <span className="inline-flex items-center gap-1 text-purple-600 text-sm font-semibold">
                                <Crown size={14} />
                                Enterprise
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {featuresToShow.map((feature, idx) => (
                            <tr 
                              key={idx} 
                              className={`transition-colors hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                            >
                              <td className="px-4 py-3 text-sm text-text-primary">{highlightMatch(feature.name)}</td>
                              <td className="px-4 py-3 text-center">{renderFeatureValue(feature.starter, 'starter')}</td>
                              <td className="px-4 py-3 text-center">{renderFeatureValue(feature.growth, 'growth')}</td>
                              <td className="px-4 py-3 text-center">{renderFeatureValue(feature.enterprise, 'enterprise')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-primary-navy text-white relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-care-teal/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-tech-blue/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why NDIS Providers Choose <span className="text-care-teal">GetCare 360</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: 'Own Your Software', desc: 'No subscription dependency - yours forever' },
              { icon: Globe, title: 'NDIS Compliance', desc: 'Built for Australian requirements' },
              { icon: Smartphone, title: 'Web + Mobile', desc: 'Branded apps included' },
              { icon: Lock, title: 'Data Ownership', desc: '100% your data, always' },
              { icon: Brain, title: 'AI-Powered', desc: 'Smart compliance & analytics' },
              { icon: Headphones, title: 'Local Support', desc: 'Australian team & hosting' },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div 
                  key={index} 
                  className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/10 hover:border-care-teal/30 hover:-translate-y-1 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-care-teal/20 flex items-center justify-center mb-4 group-hover:bg-care-teal/30 group-hover:scale-110 transition-all">
                    <Icon className="text-care-teal" size={24} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 group-hover:text-care-teal transition-colors">{item.title}</h3>
                  <p className="text-white/60 text-sm group-hover:text-white/80 transition-colors">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-tech-blue via-primary-navy to-purple-900 text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-care-teal/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }}></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
            <Award className="text-care-teal mr-2" size={16} />
            <span className="text-sm font-medium">Australian Made & Supported</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Own Your NDIS Platform — <span className="text-care-teal">Don't Rent It</span>
          </h2>
          <p className="text-xl text-white/80 mb-4 max-w-2xl mx-auto">
            Stop paying ongoing SaaS fees. Invest once and build a digital asset that grows with your organisation.
          </p>
          <p className="text-lg text-care-teal font-semibold mb-8">
            Custom-built. Fully compliant. Permanently yours.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={openBooking}
              className="bg-care-teal hover:bg-care-teal/90 text-white px-10 py-6 text-lg group shadow-lg shadow-care-teal/30 hover:shadow-care-teal/50 transition-all"
            >
              <Play className="mr-2 group-hover:scale-110 transition-transform" size={20} />
              Book a Demo
            </Button>
            <Button 
              onClick={openContact}
              variant="outline" 
              className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-6 text-lg"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Pricing Unlock Modal */}
      <PricingUnlockModal 
        isOpen={showPricingModal} 
        onClose={() => setShowPricingModal(false)}
        onSuccess={handlePricingSuccess}
      />
    </div>
  );
};

export default FeaturesAndPricingPage;
